import * as React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import { clientsApi, employeesApi } from '../api/clients.api';
import {
  companyWithContactsSchema,
  type CompanyWithContactsFormData,
} from '../schemas';
import { removeEmptyFields } from '@/lib/utils';

export function useCompanyForm() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  let { id } = useParams();
  const [searchParams] = useSearchParams();
  const manage = searchParams.get('manage');

  if (id === 'null') id = undefined;

  const form = useForm<CompanyWithContactsFormData>({
    resolver: zodResolver(companyWithContactsSchema),
    defaultValues: {
      type: 'COMPANY',
      name: '',
      inn: '',
      oked: '',
      email: '',
      phone: '',
      website: '',
      country: '',
      region: '',
      city: '',
      street: '',
      zip: '',
      building: '',
      bankName: '',
      bankCode: '',
      bankAccountNumber: '',
      contactPersons: [],
    },
  });

  const { isSubmitting } = form.formState;
  const { control } = form;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'contactPersons',
  });

  // Load existing client data for edit
  const { mutate: loadClient } = useMutation({
    mutationFn: clientsApi.getById,
    onSuccess: (data) => {
      form.reset({
        type: 'COMPANY',
        name: data.name || '',
        inn: data.inn || '',
        oked: data.oked || '',
        email: data.email || '',
        phone: data.phone || '',
        website: data.website || '',
        country: data.country || '',
        region: data.region || '',
        city: data.city || '',
        street: data.street || '',
        zip: data.zip || '',
        building: data.building || '',
        bankName: data.bankName || '',
        bankCode: data.bankCode || '',
        bankAccountNumber: data.bankAccountNumber || '',
        contactPersons: [],
      });
    },
    onError: (err: Error) => {
      toast.error(`${t('toast.error')}: ${err.message}`);
    },
  });

  // Load employees for edit mode
  const { mutate: loadEmployees } = useMutation({
    mutationFn: employeesApi.getByClientId,
    onSuccess: (data) => {
      if (data?.results) {
        form.setValue(
          'contactPersons',
          data.results.map((emp) => ({
            id: String(emp.id),
            name: emp.name || '',
            role: emp.role || emp.position || '',
            phone: emp.phone || '',
            email: emp.email || '',
          }))
        );
      }
    },
    onError: (err: Error) => {
      toast.error(`${t('toast.error')}: ${err.message}`);
    },
  });

  // Create mutation
  const { mutate: createClient } = useMutation({
    mutationFn: clientsApi.create,
    onSuccess: async (data) => {
      const contactPersons = form.getValues('contactPersons') || [];
      if (contactPersons.length > 0) {
        await Promise.all(
          contactPersons.map((person) =>
            employeesApi.create({
              ...person,
              phone: person.phone?.replace(/\D/g, '') || '',
              clientId: data.id,
            })
          )
        );
      }
      toast.success(t('toast.createdSuccess', { item: t('companyu') }));
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      navigateBack();
    },
    onError: (err: Error) => {
      toast.error(t('toast.errorCreating', { item: t('companyu'), message: err.message }));
    },
  });

  // Update mutation
  const { mutate: updateClient } = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: CompanyWithContactsFormData;
    }) => clientsApi.update(id, data),
    onSuccess: () => {
      toast.success(t('toast.updatedSuccess', { item: t('companyu') }));
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      navigateBack();
    },
    onError: (err: Error) => {
      toast.error(t('toast.errorUpdating', { item: t('companyu'), message: err.message }));
    },
  });

  React.useEffect(() => {
    if (id) {
      loadClient(id);
      loadEmployees(id);
    }
  }, [id, loadClient, loadEmployees]);

  const navigateBack = () => {
    if (manage) {
      navigate('/app/manage');
    } else {
      navigate('/app/clients');
    }
  };

  const handleSubmit = async (values: CompanyWithContactsFormData) => {
    const { contactPersons, ...clientData } = values;
    const cleanedValues = removeEmptyFields(clientData);

    if (id) {
      updateClient({ id, data: cleanedValues as CompanyWithContactsFormData });
    } else {
      createClient(cleanedValues as CompanyWithContactsFormData);
    }
  };

  return {
    form,
    fields,
    append,
    remove,
    isSubmitting,
    handleSubmit,
    navigateBack,
    id,
    manage,
  };
}
